import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import CustomApplicationState from "@thos/Store/CustomApplicationState";
import setAllSelectedToAddToList from "@thos/Store/Data/OrderHistory/Handlers/SetAllSelectedToAddToList";
import setCopyNotesSelected from "@thos/Store/Data/OrderHistory/Handlers/SetCopyNotesSelected";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const order = getOrderState(state, state.pages.orderDetails.orderNumber);
    const addToListSelectedArray = state.ThosState.OrderHistory.reOrderListArray;
    const isLineNotesIncluded = state.ThosState.OrderHistory.isCopyNotesSelected ?? false;
    const wishListSettings = getSettingsCollection(state).wishListSettings;
    return {
        isAllLineSelected: order.value?.orderLines?.length === addToListSelectedArray?.length,
        addToListSelectedArray,
        order,
        wishListSettings,
        isLineNotesIncluded
    }
}

const mapDispatchToProps = {
    setAllSelectedToAddToList,
    setAddToListModalIsOpen,
    setCopyNotesSelected
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderDetailsAddToListButtonStyles {
    selectAllCheckBoxGridItem?: GridItemProps;
    addToListAndLineNotesGridItem?: GridItemProps;
    gridContainer?: GridContainerProps;
    addSelectedToList?: GridItemProps;
    orderSummaryTextGridItem?: GridItemProps;
    selectAllGridItem?: GridItemProps;
    copyOrderLineNotesGridItem?: GridItemProps;
    titleText?: TypographyProps;
    checkBoxGroupStyles?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>
}

export const orderDetailsAddToListButtonStyles: OrderDetailsAddToListButtonStyles = {
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-top: 15px;
            margin-bottom: 0.5rem;
        `,
    },
    selectAllGridItem: {
        width: 3,
        css: css`
        justify-content: flex-end;
        `
    },
    copyOrderLineNotesGridItem: {
        width: 4,
        css: css`
        justify-content: flex-end;
        `
    },
    addSelectedToList: {
        width: 5,
        css: css`
        justify-content: center;
        `
    },
    gridContainer: {
        css: css`
        justify-content: flex-end;
        `
    },
    selectAllCheckBoxGridItem: {
        width: 6,
        align: "middle",
        css: css`
        display: flex;
            justify-content: flex-end !important;
            clear: both;
        `
    },
    orderSummaryTextGridItem: {
        width: 4,
        align: "middle",
    },
    addToListAndLineNotesGridItem: {
        width: 8,
        align: "middle",
        css: css`
        margin : 0px;
        padding : 0px;
        display: flex;
        justify-content: flex-end !important;
        clear: both;
        `
    },
    checkBoxGroupStyles: {
        css: css`
        @media print {
            display: none;
        }`
    }
}

const styles = orderDetailsAddToListButtonStyles;

const OrderDetailsAddToListButton: React.FC<Props> = ({
    order,
    addToListSelectedArray,
    isAllLineSelected,
    wishListSettings,
    setAllSelectedToAddToList,
    setAddToListModalIsOpen,
    setCopyNotesSelected,
    isLineNotesIncluded
}) => {
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }
        if (!addToListSelectedArray || addToListSelectedArray === undefined || addToListSelectedArray.length === 0) {
            return;
        }
        const orderLines: any = []
        addToListSelectedArray.forEach((lineItemId: string) => {
            const orderLine = order.value?.orderLines?.find(x => x.id === lineItemId);
            if (orderLine && orderLine != null) {
                orderLines.push({
                    productId: orderLine?.productId,
                    qtyOrdered: orderLine?.qtyOrdered,
                    unitOfMeasure: orderLine?.unitOfMeasure,
                    notes: orderLine?.notes
                })
            }
        });
        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: orderLines });
    };

    const setAllSelectedClickHandler = (value: boolean) => {
        setAllSelectedToAddToList({ allChecked: value })
    }

    const setIncludeLineNotesClickHandler = (value: boolean) => {
        setCopyNotesSelected({ isCopyNotesSelected: value })
    }

    return (
        <>
            <Hidden below="md">
                <GridContainer {...styles.gridContainer}>
                    <GridItem {...styles.orderSummaryTextGridItem}>
                        <Typography {...styles.titleText}>{translate("Order Summary")}</Typography>
                    </GridItem>
                    <GridItem {...styles.addToListAndLineNotesGridItem} >
                        <GridItem {...styles.selectAllGridItem} >
                            <CheckboxGroup {...styles.checkBoxGroupStyles} key={Math.random()}>
                                <Checkbox checked={isAllLineSelected} onChange={(e, value) => setAllSelectedClickHandler(value)} key={Math.random()}>{translate("Select All")}</Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                        <GridItem {...styles.copyOrderLineNotesGridItem}>
                            <CheckboxGroup {...styles.checkBoxGroupStyles} key={Math.random()}>
                                <Checkbox checked={isLineNotesIncluded} onChange={(e, value) => setIncludeLineNotesClickHandler(value)}> {translate("Copy Order Line Notes")}</Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                        <GridItem {...styles.addSelectedToList}>
                            <Button onClick={addToListClickHandler} disabled={addToListSelectedArray === undefined || addToListSelectedArray?.length === 0}>
                                {translate("Add Selected To List")}
                            </Button>
                        </GridItem>
                    </GridItem>
                </GridContainer>
            </Hidden>
            <Hidden above="sm">
                <GridContainer>
                    <GridItem width={4} align="middle">
                        <CheckboxGroup {...styles.checkBoxGroupStyles}>
                            <Checkbox checked={isAllLineSelected} onChange={(e, value) => setAllSelectedClickHandler(value)} key={Math.random()}>{translate("Select All")} </Checkbox>
                        </CheckboxGroup>
                    </GridItem>
                    <GridItem width={6} align="middle">
                        <CheckboxGroup {...styles.checkBoxGroupStyles}>
                            <Checkbox checked={isLineNotesIncluded} onChange={(e, value) => setIncludeLineNotesClickHandler(value)}> {translate("Copy Order Line Notes")}</Checkbox>
                        </CheckboxGroup>
                    </GridItem>
                    <GridItem width={2}>
                        <OverflowMenu position="end" >
                            <Clickable onClick={addToListClickHandler} disabled={addToListSelectedArray === undefined || addToListSelectedArray?.length === 0}>
                                {translate("Add Selected To List")}
                            </Clickable>
                        </OverflowMenu>
                    </GridItem>
                </GridContainer>
            </Hidden>
        </>
    )
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderDetailsAddToListButton)),
    definition: {
        allowedContexts: ["OrderDetailsPage"],
        displayName: "Order Details Add To List Button",
        group: "Order Details",
    },
};

export default widgetModule;